import { useEffect, useRef } from "react";
import L from "leaflet";
import { MapContainer, TileLayer, useMap } from "react-leaflet";
import { boundsFromRadiusMeters } from "../../lib/geo";
import {
  formatPlaceCategoryLabel,
  formatPlaceCategoryStars,
  formatPlaceDistance,
} from "../../services/placeService";

const DARK_TILES = "https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png";
const ATTR =
  '&copy; <a href="https://openstreetmap.org">OSM</a> · <a href="https://carto.com/attributions">CARTO</a>';

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function buildPlacePopupHtml(place, selected) {
  const name = escapeHtml(place.name);
  const locality = escapeHtml(
    [place.addressLocality, place.addressCountry].filter(Boolean).join(", ")
  );
  const distanceLabel = escapeHtml(formatPlaceDistance(place) || "");
  const stars = formatPlaceCategoryStars(place.category);
  const categoryLabel = formatPlaceCategoryLabel(place.category);
  const starsHtml =
    stars && categoryLabel
      ? `<div style="margin-top:8px;font-family:'Space Mono',monospace;font-size:13px;letter-spacing:0.12em;color:#e9b5c5;">${escapeHtml(stars)}</div>
         <div style="font-family:'Space Mono',monospace;font-size:9px;letter-spacing:0.18em;text-transform:uppercase;color:rgba(255,255,255,0.45);margin-top:4px;">${escapeHtml(categoryLabel)}</div>`
      : categoryLabel
        ? `<div style="font-family:'Space Mono',monospace;font-size:10px;letter-spacing:0.14em;color:rgba(255,255,255,0.55);margin-top:8px;">${escapeHtml(categoryLabel)}</div>`
        : "";

  return `
    <div style="min-width:220px">
      <div style="font-family:'Cormorant Garamond',serif;font-size:18px;color:#f5f5f5;letter-spacing:0.02em;">${name}</div>
      ${starsHtml}
      ${
        locality
          ? `<div style="font-family:'Space Mono',monospace;font-size:10px;letter-spacing:0.14em;color:rgba(255,255,255,0.55);margin-top:8px;">${locality}</div>`
          : ""
      }
      ${
        distanceLabel
          ? `<div style="font-family:'Space Mono',monospace;font-size:10px;letter-spacing:0.18em;text-transform:uppercase;color:#e9b5c5;margin-top:8px;">${distanceLabel}</div>`
          : ""
      }
      <button type="button" id="zx-place-popup-${place.uuid}" style="margin-top:14px;width:100%;background:${selected ? "rgba(139,30,63,0.55)" : "#8B1E3F"};color:#f5f5f5;border:none;padding:10px 12px;font-family:'Space Mono',monospace;text-transform:uppercase;letter-spacing:0.18em;font-size:10px;cursor:pointer;">${selected ? "Seleccionado ✓" : "Escoger este lugar →"}</button>
    </div>
  `;
}

function MapResize({ active }) {
  const map = useMap();
  useEffect(() => {
    if (!active) return;
    const refresh = () => map.invalidateSize({ animate: false });
    refresh();
    const timers = [80, 200, 400].map((ms) => setTimeout(refresh, ms));
    map.whenReady(refresh);
    return () => timers.forEach(clearTimeout);
  }, [active, map]);
  return null;
}

function FitView({ userPosition, places, radiusKm, active }) {
  const map = useMap();
  useEffect(() => {
    if (!active || !userPosition) return;
    const apply = () => {
      const [[south, west], [north, east]] = boundsFromRadiusMeters(
        userPosition.lat,
        userPosition.lng,
        radiusKm * 1000
      );
      const bounds = L.latLngBounds([south, west], [north, east]);
      places.forEach((place) => {
        if (place.lat != null && place.lng != null) {
          bounds.extend([place.lat, place.lng]);
        }
      });
      map.invalidateSize({ animate: false });
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [32, 32], maxZoom: 14 });
      } else {
        map.setView([userPosition.lat, userPosition.lng], 12);
      }
    };
    map.whenReady(apply);
  }, [active, userPosition, places, radiusKm, map]);
  return null;
}

function RadiusCircle({ center, radiusKm }) {
  const map = useMap();
  const ref = useRef(null);
  useEffect(() => {
    if (!center) return;
    if (ref.current) {
      map.removeLayer(ref.current);
      ref.current = null;
    }
    ref.current = L.circle([center.lat, center.lng], {
      radius: radiusKm * 1000,
      color: "#8B1E3F",
      weight: 1,
      opacity: 0.45,
      fillColor: "#8B1E3F",
      fillOpacity: 0.05,
      interactive: false,
    }).addTo(map);
    return () => {
      if (ref.current) map.removeLayer(ref.current);
    };
  }, [center, radiusKm, map]);
  return null;
}

function PlacesMarkers({ places, selectedUuid, onSelectPlace }) {
  const map = useMap();
  const layerRef = useRef(null);

  useEffect(() => {
    if (!layerRef.current) {
      layerRef.current = L.layerGroup().addTo(map);
    }
    const layer = layerRef.current;
    layer.clearLayers();

    places.forEach((place) => {
      if (place.lat == null || place.lng == null) return;
      const selected = place.uuid === selectedUuid;
      const html = `<div class="zx-marker place${selected ? " selected" : ""}"></div>`;
      const icon = L.divIcon({
        className: "zx-divicon",
        html,
        iconSize: [20, 20],
        iconAnchor: [10, 10],
      });
      const marker = L.marker([place.lat, place.lng], { icon });
      marker.bindPopup(buildPlacePopupHtml(place, selected), {
        closeButton: true,
        autoPan: true,
        className: "zx-place-popup",
      });
      marker.on("click", () => marker.openPopup());
      marker.on("popupopen", () => {
        const btn = document.getElementById(`zx-place-popup-${place.uuid}`);
        if (btn) {
          btn.onclick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            onSelectPlace?.(place);
          };
        }
      });
      marker.addTo(layer);
    });
  }, [places, selectedUuid, map, onSelectPlace]);

  return null;
}

function UserMarker({ userPosition }) {
  const map = useMap();
  const layerRef = useRef(null);

  useEffect(() => {
    if (!userPosition) return;
    if (!layerRef.current) {
      layerRef.current = L.layerGroup().addTo(map);
    }
    const layer = layerRef.current;
    layer.clearLayers();
    const icon = L.divIcon({
      className: "zx-divicon",
      html: `<div class="zx-marker self"></div>`,
      iconSize: [20, 20],
      iconAnchor: [10, 10],
    });
    L.marker([userPosition.lat, userPosition.lng], { icon, interactive: false }).addTo(layer);
    return () => layer.clearLayers();
  }, [userPosition, map]);

  return null;
}

export default function PlacesMap({
  userPosition,
  places = [],
  radiusKm = 25,
  selectedUuid = null,
  onSelectPlace,
  active = true,
}) {
  const center = userPosition || { lat: 40.4168, lng: -3.7038 };

  return (
    <div
      className="relative z-0 h-full min-h-[280px] w-full"
      data-testid="places-map"
    >
      <MapContainer
        center={[center.lat, center.lng]}
        zoom={12}
        className="zx-places-map-container"
        style={{ height: "100%", minHeight: 280, width: "100%", background: "#050505" }}
        zoomControl
        attributionControl
      >
        <TileLayer attribution={ATTR} url={DARK_TILES} />
        <MapResize active={active} />
        <FitView
          active={active}
          userPosition={userPosition}
          places={places}
          radiusKm={radiusKm}
        />
        <RadiusCircle center={userPosition} radiusKm={radiusKm} />
        <UserMarker userPosition={userPosition} />
        <PlacesMarkers
          places={places}
          selectedUuid={selectedUuid}
          onSelectPlace={onSelectPlace}
        />
      </MapContainer>
    </div>
  );
}
